import { CopyOutlined } from '@ant-design/icons';
import { Alert, Button, Card, message, Space, Tag } from 'antd';
import getOtpApiCall from 'apps/investment/apiCalls/bank-accounts/nhsv-get-otp';
import { companyColors } from 'apps/investment/constants';
import CashSVG from 'common/svg/cash-svg';
import InfoCard from 'common/ui/info-card';
import { ENV_NAME } from 'config/env-config';
import { usePostApi } from 'hooks/api';
import { capitalize } from 'lodash';
import { LandmarkIcon, UserIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime } from 'utils/formatDate';

const bankAccountStatusColors = {
  pending: 'orange',
  success: 'green',
  deleting: 'yellow',
  deleted: 'grey',
  failed: 'red',
};

const ItemDetail = ({ item }) => {
  const [otpData, setOtpData] = useState<any>(null);
  const isProd = ENV_NAME === 'production';
  const showOtpSection = !isProd && !!item?.nhsv_trans_id;
  const { post: requestOtp, isLoading: isRequestingOtp } = usePostApi(
    getOtpApiCall,
    {
      onSuccess: responseData => {
        const otp = responseData?.data?.otp;
        const apiMessage = responseData?.data?.message;
        setOtpData({
          otp,
          message: apiMessage,
          timestamp: new Date(),
        });
        message.success(
          'OTP đã được lấy thành công!' + (otp ? ` Mã OTP: ${otp}` : '')
        );
      },
    }
  );

  const handleCopyOtp = () => {
    if (otpData?.otp) {
      navigator.clipboard.writeText(otpData.otp.toString());
      message.success('Đã sao chép OTP!');
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard
          label="Status"
          value={
            item.status ? (
              <Tag color={bankAccountStatusColors[item.status] || 'default'}>
                {capitalize(item.status)}
              </Tag>
            ) : (
              '-'
            )
          }
        />
        <InfoCard
          label="Trading Account Manager"
          value={
            item.trading_account_manager ? (
              <Tag color={companyColors[item.trading_account_manager]}>
                {item.trading_account_manager}
              </Tag>
            ) : (
              '-'
            )
          }
        />
        <InfoCard
          label="Trading Account Number"
          value={item.account_number || '-'}
        />
      </div>

      {/* User & Account Section */}
      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
        <UserIcon className="mr-1" size={18} />
        User & Account
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard
          label="User Account"
          value={
            item.user ? (
              <Link to={`/investment/users/${item.user.id || item.user_id}`}>
                <div>{item.user.name}</div>
                <div className="text-xs text-gray-500">{item.user.email}</div>
              </Link>
            ) : (
              '-'
            )
          }
        />
        {item.user?.phone && <InfoCard label="Phone" value={item.user.phone} />}
        <InfoCard
          label="Stock Account"
          value={
            item.account_number ? (
              <Link
                to={`/investment/stock-accounts?search=${item.account_number}`}
              >
                {item.account_number}
              </Link>
            ) : (
              '-'
            )
          }
        />
        <InfoCard
          label="NHSV Transaction ID"
          value={item.nhsv_trans_id || '-'}
        />
      </div>

      {/* Bank Information Section */}
      <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
        <LandmarkIcon className="mr-1" size={18} />
        Bank Information
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard
          label="Bank"
          value={
            item.bank ? (
              <div>
                <div>{item.bank.name}</div>
                <div className="text-xs text-gray-500">
                  Code: {item.bank.code}
                </div>
              </div>
            ) : (
              item.bank_name || '-'
            )
          }
        />
        <InfoCard label="Bank Branch" value={item.bank_branch_name || '-'} />
        <InfoCard label="Account Name" value={item.bank_account_name || '-'} />
        <InfoCard
          label="Account Number"
          value={item.bank_account_number || '-'}
        />
      </div>

      {/* OTP Section */}
      {showOtpSection && (
        <>
          <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
            <CashSVG className="mr-1" />
            OTP SMS
          </h3>
          <div>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                onClick={() => requestOtp({ id: item.id })}
                loading={isRequestingOtp}
              >
                Get OTP
              </Button>
              <div className="text-xs text-gray-500 max-w-md">
                Chỉ dùng ở môi trường test/staging (không áp dụng Production).
                OTP sẽ được lấy theo NHSV Transaction ID hiện tại.
              </div>
              {otpData?.otp && (
                <Card
                  size="small"
                  className="bg-green-50 border-green-300"
                  style={{ marginTop: '8px' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 font-medium">Mã OTP:</span>
                      <Tag
                        color="green"
                        className="text-lg font-bold px-3 py-1"
                        style={{ height: 'auto' }}
                      >
                        {otpData.otp}
                      </Tag>
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={handleCopyOtp}
                        title="Sao chép OTP"
                      />
                    </div>
                    {otpData.message && (
                      <Alert
                        message={otpData.message}
                        type="success"
                        showIcon
                        style={{ margin: 0 }}
                      />
                    )}
                  </Space>
                </Card>
              )}
            </Space>
          </div>
        </>
      )}

      {/* Timestamps Footer */}
      <div className="bg-gray-50 -mx-6 -mb-6 p-6 rounded-b-lg mt-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <InfoCard
            label="Created At"
            value={formatDateTime(item.created_at)}
          />
          <InfoCard
            label="Updated At"
            value={formatDateTime(item.updated_at)}
          />
        </div>
      </div>
    </div>
  );
};

export default ItemDetail;
